import { createContext, useContext, useEffect, useState } from 'react';

/**
 * UnitsProvider — keeps the user's preferred distance unit ('km' or 'mi')
 * persisted in localStorage and exposes it to the whole app.
 *
 * Why a context (not just a hook): the value needs to flip *every*
 * unit-rendering component in the app the moment the user toggles in Settings.
 */
const UnitsContext = createContext({
  units: 'km',
  setUnits: () => {},
});

const STORAGE_KEY = 'rivl:units';

export function UnitsProvider({ children }) {
  const [units, setUnitsState] = useState(() => {
    if (typeof window === 'undefined') return 'km';
    return window.localStorage.getItem(STORAGE_KEY) || 'km';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, units);
    } catch {
      /* ignore */
    }
  }, [units]);

  return (
    <UnitsContext.Provider value={{ units, setUnits: setUnitsState }}>
      {children}
    </UnitsContext.Provider>
  );
}

export function useUnits() {
  return useContext(UnitsContext);
}

// ----------------------------------------------------------------------------
// Pure helpers — safe to import without the provider (default to km).
// ----------------------------------------------------------------------------

export const KM_PER_MILE = 1.609344;

export function kmToDisplay(km, units) {
  if (km == null || Number.isNaN(km)) return 0;
  return units === 'mi' ? km / KM_PER_MILE : km;
}

export function unitLabel(units, { plural = false } = {}) {
  if (units === 'mi') return plural ? 'miles' : 'mi';
  return 'km';
}

// secPerKm → secPerUnit (mile-pace ≈ km-pace × 1.609)
export function paceSecPerUnit(secPerKm, units) {
  if (!secPerKm) return secPerKm;
  return units === 'mi' ? secPerKm * KM_PER_MILE : secPerKm;
}

// Event distance helper: "5K" (km mode) or "3.1 mi" (mile mode).
export function formatEventDistance(km, units) {
  const v = kmToDisplay(Number(km) || 0, units);
  if (units === 'mi') return `${v.toFixed(1)} mi`;
  return `${km}K`;
}
